import {INFLUENCER_KEYS} from "@/api/influencerApi";
import type {InfluencerProfileDetailSerializer, OpenCampaignSerializer} from "@/generated/DjangoTypes";
import {queryClient} from "@/queryClient";
import {create} from "@/store/createStore";
import {
  buildActiveFilters,
  captureSearchParamsDiffEvents,
  createDrawerActions,
  DEFAULT_FILTERS,
  DEFAULT_INITIAL_FILTERS,
  mergeFiltersPreservingCurrent,
  type TFilters,
  type TInitialFilters,
} from "@/store/filterStoreUtils";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import type {TActiveFilter, TCampaignType, TNonFunctionKeys, TPaginated} from "@/store/types";
import useUserStore from "@/store/user";
import {type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {getDefaultStore} from "jotai";
import {debounce, DebouncedFuncLeading, omit} from "lodash";
import qs from "qs";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TPlatformFilters = Omit<TFilters, "ordering" | "favourites_first" | "recommended" | "type">;

type TState = {
  activeFilters: TActiveFilter[];
  defaultFilters: TFilters;
  filters: TFilters;
  initialFilters: TInitialFilters;
  initializedPlatforms: Record<string, boolean>;
  isFilterDrawerOpen: boolean;
  pendingSearchParams: string | null;
  openCampaignsOfCompany: OpenCampaignSerializer[];
  platformFilterStash: Partial<Record<TCampaignType, Partial<TPlatformFilters>>>;
  savedFilters: TFilters | null;
  searchParams: string | null;
  debouncedPendingSearchParams: DebouncedFuncLeading<() => void>;
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  applyDrawerFilters(): void;
  buildSearchParams(): string;
  cancelDrawerFilters(): void;
  getHistogram<
    K extends
      | "followers__histogram"
      | "engagement__histogram"
      | "median_views__histogram"
      | "tiktok_last_period_video_count__histogram",
  >(
    key: K,
  ): TState["initialFilters"][K];
  openDrawer(): void;
  reset(): void;
  resetAllFilters(): void;
  resetFilter(filterKeys: string[]): void;
  setFilters<K extends keyof TFilters, V extends TFilters[K]>(key: K, value: V, options?: {skipQuery?: boolean}): void;
  setInitialFilters(filters: TState["initialFilters"]): void;
  setSearchParams(searchParams: string, options?: {skipAnalytics?: boolean}): void;
  setValue<K extends TNonFunctionKeys<TState>, V extends TState[K]>(key: K, value: V): void;
  switchType(newType: TCampaignType): void;
  updateActiveFilters(): void;
};

const initialState = {
  activeFilters: [],
  defaultFilters: {...DEFAULT_FILTERS},
  initializedPlatforms: {},
  isFilterDrawerOpen: false,
  pendingSearchParams: null,
  savedFilters: null,
  searchParams: null,
  initialFilters: {...DEFAULT_INITIAL_FILTERS},
  filters: {...DEFAULT_FILTERS, ordering: "-recommended"},
  openCampaignsOfCompany: [],
  platformFilterStash: {},
} as Pick<TState, TNonFunctionKeys<TState>>;

const useInfluencerNetworkStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        debouncedPendingSearchParams: debounce(() => {
          const pendingParams = get().buildSearchParams();

          set(
            state => {
              state.pendingSearchParams = pendingParams;
            },
            false,
            "influencerNetworkStore/setPendingSearchParams",
          );
        }, 1000),
        debouncedSearchParams: debounce(() => {
          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams);
        }, 1000),
        buildSearchParams: () => {
          const isAdmin = useUserStore.getState().user.user_type === "admin";

          if (isAdmin && get().filters.ordering === "-recommended") {
            set(state => {
              state.filters.ordering = "-user__last_app_login";
            });
          }

          if (!get().filters.ordering) {
            set(state => {
              state.filters.ordering = isAdmin ? "-user__last_app_login" : "-recommended";
            });
          }

          if (get().filters.ordering === "-recommended") {
            set(state => {
              state.filters.recommended = true;
              state.filters.favourites_first = null;
            });
          } else if (get().filters.ordering === "favourites_first") {
            set(state => {
              state.filters.favourites_first = true;
              state.filters.recommended = null;
            });
          } else {
            set(state => {
              state.filters.recommended = null;
              state.filters.favourites_first = null;
            });
          }

          const filters = get().filters;
          const filtersWithoutRecommendedAndFavourites = omit(filters, ["recommended", "favourites_first"]);
          const params = isAdmin
            ? filtersWithoutRecommendedAndFavourites
            : get().filters.ordering === "-recommended" || get().filters.ordering === "favourites_first"
              ? {...filters, ordering: null}
              : filters;

          const serializedParams = {
            ...params,
            audience_country: filters.audience_country.join(","),
            audience_location_search: filters.audience_location_search.map(item => item.name).join(","),
            company_id: getDefaultStore().get(selectedCompanyIdAtom) ?? undefined,
            cutoff_value: filters.vibe ? filters.cutoff_value : undefined,
            influencer_country: filters.influencer_country.join(","),
            maximum_cutoff_value: filters.vibe ? filters.maximum_cutoff_value : undefined,
            feed_hashtags:
              filters.is_competitor_filter_active && filters.feed_hashtags.length > 0
                ? filters.feed_hashtags.join(",")
                : undefined,
            feed_hashtags_mode:
              filters.is_competitor_filter_active && filters.feed_hashtags.length > 0
                ? filters.feed_hashtags_mode
                : undefined,
            feed_tags:
              filters.is_competitor_filter_active && filters.feed_tags.length > 0
                ? filters.feed_tags.join(",")
                : undefined,
            feed_tags_mode:
              filters.is_competitor_filter_active && filters.feed_tags.length > 0 ? filters.feed_tags_mode : undefined,
            location_radius_km:
              filters.location_radius_km > 0 && filters.location_search.some(item => item.type !== "region")
                ? filters.location_radius_km
                : undefined,
            location_search: filters.location_search
              .filter(item => item.type !== "zip")
              .map(item => item.name)
              .join(","),
            location_zips: filters.location_search
              .filter(item => item.type === "zip")
              .map(item => `${item.country_code}:${item.name}`)
              .join(","),
            new_on_platform: filters.new_on_platform ? true : undefined,
          };

          return qs.stringify(serializedParams, {arrayFormat: "comma"});
        },
        setSearchParams: (searchParams, options) => {
          const previous = get().searchParams;
          set(
            state => {
              state.searchParams = searchParams;
            },
            false,
            "influencerNetworkStore/setSearchParams",
          );
          if (!options?.skipAnalytics) {
            captureSearchParamsDiffEvents(previous, searchParams, "network");
          }
        },
        setInitialFilters: filters => {
          const user = useUserStore.getState().user;
          const isAdmin = user.user_type === "admin";
          const currentType = get().filters.type;
          const alreadyInitialized = get().initializedPlatforms[currentType];

          set(
            state => {
              state.initialFilters = {
                ...state.initialFilters,
                ...filters,
              };

              // Only set filter range values on first init per platform
              if (!alreadyInitialized) {
                state.filters.followers_start = filters.followers__min;
                state.filters.followers_end = filters.followers__max;
                state.filters.median_views_start = filters.median_views__min;
                state.filters.median_views_end = filters.median_views__max;
                state.filters.engagement_start = filters.engagement__min;
                state.filters.engagement_end = filters.engagement__max;
                state.filters.tiktok_last_period_video_count_start = filters.tiktok_last_period_video_count__min;
                state.filters.tiktok_last_period_video_count_end = filters.tiktok_last_period_video_count__max;

                if (isAdmin) {
                  state.filters.ordering = "-user__last_app_login";
                }
              }

              state.initializedPlatforms[currentType] = true;
            },
            false,
            "influencerNetworkStore/setInitialFilters",
          );

          if (!alreadyInitialized) {
            set(
              state => {
                state.defaultFilters = {...state.filters};
              },
              false,
              "influencerNetworkStore/setDefaultFilters",
            );
          }

          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams);
          get().updateActiveFilters();
        },
        ...createDrawerActions(get, set, "influencerNetworkStore"),
        setFilters: (key, value, options = {skipQuery: false}) => {
          get().debouncedSearchParams.cancel();

          set(
            state => {
              state.filters[key] = value;

              if (key !== "cutoff_value" && key !== "maximum_cutoff_value" && key !== "vibe") {
                state.filters.cutoff_value = DEFAULT_FILTERS.cutoff_value;
                state.filters.maximum_cutoff_value = DEFAULT_FILTERS.maximum_cutoff_value;
              }
            },
            false,
            "influencerNetworkStore/setFilters",
          );

          if (options.skipQuery) {
            return;
          }

          if (get().isFilterDrawerOpen) {
            get().debouncedPendingSearchParams();
            get().updateActiveFilters();

            return;
          }

          const searchParams = get().buildSearchParams();
          const cachedQuery = queryClient.getQueryData<
            InfiniteData<AxiosResponse<TPaginated<InfluencerProfileDetailSerializer>>>
          >(INFLUENCER_KEYS.qInfluencerList(searchParams));

          if (cachedQuery?.pages) {
            get().debouncedSearchParams.cancel();
            get().setSearchParams(searchParams);
          } else {
            get().debouncedSearchParams();
          }

          get().updateActiveFilters();
        },
        setValue: (key, value) =>
          set(
            state => {
              state[key] = value;
            },
            false,
            "influencerNetworkStore/setValue",
          ),
        resetAllFilters: () => {
          const current = get().filters;
          set(
            state => {
              state.filters = mergeFiltersPreservingCurrent(current, state.defaultFilters, {type: current.type});
            },
            false,
            "influencerNetworkStore/resetAllFilters",
          );

          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams, {skipAnalytics: true});
          get().updateActiveFilters();
        },
        resetFilter: filterKeys => {
          const defaults = get().defaultFilters;

          set(
            state => {
              state.filters = {
                ...state.filters,
                ...Object.fromEntries(filterKeys.map(key => [key, defaults[key as keyof TFilters]])),
              };
            },
            false,
            "influencerNetworkStore/resetFilter",
          );

          const pendingParams = get().buildSearchParams();

          set(
            state => {
              state.pendingSearchParams = pendingParams;
            },
            false,
            "influencerNetworkStore/resetFilter/setPendingSearchParams",
          );

          get().updateActiveFilters();
        },
        updateActiveFilters: () => {
          const filters = get().filters;
          const initialFilters = get().initialFilters;
          const active = buildActiveFilters(filters, initialFilters);

          set(
            state => {
              state.activeFilters = active;
            },
            false,
            "influencerNetworkStore/updateActiveFilters",
          );
        },
        getHistogram: key => get().initialFilters[key],
        reset: () => {
          set(initialState, false, "influencerNetworkStore/reset");
        },
        switchType: newType => {
          const {filters, defaultFilters} = get();
          const oldType = filters.type;

          if (oldType === newType) return;

          const {ordering, favourites_first, recommended, type: _, ...stash} = filters;
          const restored = get().platformFilterStash[newType];

          set(
            state => {
              state.platformFilterStash[oldType] = stash;
              delete state.platformFilterStash[newType];

              Object.assign(state.filters, defaultFilters, restored ?? {}, {
                favourites_first,
                ordering,
                recommended,
                type: newType,
              });
            },
            false,
            "influencerNetworkStore/switchType",
          );

          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams);
          get().updateActiveFilters();
        },
      }),
      {name: "useInfluencerNetworkStore"},
    ),
  ),
);

export default useInfluencerNetworkStore;
